import { House } from '../../data/houses';
import { Sparkles } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';

interface ScanSuccessProps {
  house: House;
  response?: {
    acornsRedeemed: number;
    points: number;
    pointsAdded: number;
    badges: Array<{
      BadgeName: string;
      BadgeDescription: string;
      BadgeId: string;
      CurrentXp: number;
      LevelNumber: number | null;
      LevelName: string | null;
      BadgeImageUrl: string;
      LevelImageUrl: string | null;
    }>;
  };
  // Legacy props for backward compatibility
  pointsAdded?: number;
  oldPoints?: number;
  onComplete: () => void;
}

export default function ScanSuccess({
  house,
  response,
  pointsAdded: legacyPointsAdded,
  oldPoints: legacyOldPoints,
  onComplete,
}: ScanSuccessProps) {
  // Determine points from response or legacy props
  // response.points is the NEW total balance, response.pointsAdded is what was just added
  const newBalance = response ? Number(response.points) : ((legacyOldPoints ?? 0) + (legacyPointsAdded ?? 0));
  const pointsAdded = response ? Number(response.pointsAdded) : (legacyPointsAdded ?? 0);
  const oldBalance = newBalance - pointsAdded;

  const [displayPoints, setDisplayPoints] = useState(oldBalance);
  const hasRunRef = useRef(false);

  useEffect(() => {
    // Only run once per mount
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const duration = 1000;
    const steps = 30;
    const increment = pointsAdded / steps;
    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      if (currentStep <= steps) {
        setDisplayPoints(Math.floor(oldBalance + increment * currentStep));
      } else {
        setDisplayPoints(newBalance);
        clearInterval(interval);
      }
    }, duration / steps);

    const timeout = setTimeout(() => {
      onComplete();
    }, 2000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        className="relative backdrop-blur-xl bg-white/20 rounded-3xl p-12 shadow-2xl border-2 animate-scale-in"
        style={{
          borderColor: house.color,
          boxShadow: `0 8px 32px ${house.color}80, 0 0 60px ${house.color}60`,
        }}
      >
        <div className="flex flex-col items-center space-y-6">
          <div className="relative">
            <Sparkles
              size={80}
              className="animate-spin-slow"
              style={{ color: house.accent }}
            />
            <Sparkles
              size={40}
              className="absolute top-0 right-0 animate-ping"
              style={{ color: house.accent }}
            />
            <Sparkles
              size={40}
              className="absolute bottom-0 left-0 animate-ping"
              style={{ color: house.accent }}
            />
          </div>

          <div
            className="text-5xl font-bold animate-bounce"
            style={{ color: house.accent }}
          >
            +{pointsAdded} POINTS
          </div>

          <div className="flex items-center space-x-3">
            <span className="text-6xl">{house.icon}</span>
            <h2
              className="text-4xl font-bold tracking-wider"
              style={{ color: house.accent }}
            >
              {house.name.toUpperCase()}
            </h2>
          </div>

          <div className="flex items-center space-x-4 text-3xl font-bold text-white">
            <span style={{ color: house.accent }}>{oldBalance}</span>
            <span>→</span>
            <span style={{ color: house.accent }}>{displayPoints}</span>
          </div>
        </div>

        <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-sparkle"
              style={{
                backgroundColor: house.accent,
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
                animationDelay: `${Math.random() * 2}s`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
